// Canvas — höger spalt i designläget. Renderar envelope-vy (body-bg + 600px
// content-kolumn med content-bg) och slussar block-listan vidare till
// BlockList som sköter drop-zoner, hover-toolbar och selection.
//
// Drag-and-drop, block-actions och nestad redigering i twoColumns ligger
// numera i BlockList — den här komponenten är bara den yttre ramen.

import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockList } from './blockList';
import { BlockCanvasProvider, BlockCanvasContextValue } from './canvasContext';
import { DropTarget, findBlockParent } from './blockOps';
import { hasDragPayload, readDragPayload } from './dnd';
import {
  Block,
  BlockId,
  BlockType,
  CONTENT_MAX_WIDTH_PX,
  EmailDocSettings,
  ENVELOPE_BOTTOM_SPACER_PX,
  ENVELOPE_GUTTER_PX,
  ENVELOPE_TOP_SPACER_PX,
} from './types';

type Props = {
  blocks: Block[];
  selectedBlockId: BlockId | null;
  docSettings: EmailDocSettings;
  onSelectBlock: (id: BlockId | null) => void;
  onInsertBlock: (type: BlockType, target: DropTarget) => void;
  onReorderBlock: (id: BlockId, target: DropTarget) => void;
  onMoveBlock: (id: BlockId, direction: 'up' | 'down') => void;
  onDuplicateBlock: (id: BlockId) => void;
  onDeleteBlock: (id: BlockId) => void;
};

export const BlockCanvas = ({
  blocks,
  selectedBlockId,
  docSettings,
  onSelectBlock,
  onInsertBlock,
  onReorderBlock,
  onMoveBlock,
  onDuplicateBlock,
  onDeleteBlock,
}: Props) => {
  const { t } = useTranslation('groupMessage');
  const [activeDragTarget, setActiveDragTarget] = useState<{ listId: string; index: number } | null>(null);

  // Global drag-cleanup: när ett drag avslutas (drop eller cancel) rensas
  // activeDragTarget så att indikatorer inte stannar kvar.
  useEffect(() => {
    const clear = () => setActiveDragTarget(null);
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    return () => {
      window.removeEventListener('dragend', clear);
      window.removeEventListener('drop', clear);
    };
  }, []);

  const ctxValue: BlockCanvasContextValue = {
    selectedBlockId,
    docSettings,
    onSelectBlock,
    onInsertBlock,
    onReorderBlock,
    onMoveBlock,
    onDuplicateBlock,
    onDeleteBlock,
    activeDragTarget,
    setActiveDragTarget,
    getMoveCapabilities: (id) => {
      const parent = findBlockParent(blocks, id);
      if (!parent) return { canMoveUp: false, canMoveDown: false };
      return {
        canMoveUp: parent.index > 0,
        canMoveDown: parent.index < parent.siblings.length - 1,
      };
    },
  };

  // Tom canvas: dropzone som accepterar drops på index 0.
  const handleEmptyDragOver = (e: React.DragEvent) => {
    if (!hasDragPayload(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleEmptyDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const payload = readDragPayload(e);
    if (!payload) return;
    const target: DropTarget = { kind: 'top', index: 0 };
    if (payload.kind === 'new') {
      onInsertBlock(payload.blockType, target);
    } else {
      onReorderBlock(payload.blockId, target);
    }
  };

  return (
    <BlockCanvasProvider value={ctxValue}>
      <Box
        onClick={() => onSelectBlock(null)}
        sx={{
          minHeight: '100%',
          bgcolor: docSettings.bodyBgColor,
          pt: `${ENVELOPE_TOP_SPACER_PX}px`,
          pb: `${ENVELOPE_BOTTOM_SPACER_PX}px`,
          px: `${ENVELOPE_GUTTER_PX}px`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          transition: 'background-color 120ms ease',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: CONTENT_MAX_WIDTH_PX,
            bgcolor: docSettings.contentBgColor,
            minHeight: 200,
            transition: 'background-color 120ms ease',
          }}
        >
          {blocks.length === 0 ? (
            <Box onDragOver={handleEmptyDragOver} onDrop={handleEmptyDrop} sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 500, mb: 1 }}>
                {t('builder.canvas.emptyTitle')}
              </Typography>
              <Typography variant='body2'>{t('builder.canvas.emptyBody')}</Typography>
            </Box>
          ) : (
            <BlockList blocks={blocks} targetFactory={(index) => ({ kind: 'top', index })} />
          )}
        </Box>
      </Box>
    </BlockCanvasProvider>
  );
};

export default BlockCanvas;
